"use client"

import type { ComponentProps, ReactNode } from "react"

import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { cn } from "@/lib/utils"

type InputMode = ComponentProps<typeof Input>["inputMode"]
type InputType = ComponentProps<typeof Input>["type"]

interface PaymentFieldProps {
  id: string
  name: string
  label: string
  placeholder: string
  value: string
  onChange: (value: string) => void
  inputMode?: InputMode
  type?: InputType
  disabled?: boolean
}

interface WalletButton {
  id: string
  label: string
  icon?: ReactNode
  onClick?: () => void
  disabled?: boolean
}

interface CheckoutAction {
  label: string
  onClick?: () => void
  disabled?: boolean
  href?: string
  target?: string
  rel?: string
}

export interface RuixenPaymentCardProps {
  walletButtons: WalletButton[]
  separatorLabel: string
  cardholder: PaymentFieldProps
  cardNumber: PaymentFieldProps
  expiryCvvLabel: string
  expiry: PaymentFieldProps
  cvv: PaymentFieldProps
  checkout: CheckoutAction
  className?: string
}

export default function RuixenPaymentCard({
  walletButtons,
  separatorLabel,
  cardholder,
  cardNumber,
  expiryCvvLabel,
  expiry,
  cvv,
  checkout,
  className,
}: RuixenPaymentCardProps): JSX.Element {
  return (
    <div className={cn("flex items-center justify-center p-4", className)}>
      <Card className="w-full max-w-md rounded-2xl shadow-lg">
        <CardContent className="space-y-6 p-6">
          <div className="grid grid-cols-3 gap-4">
            {walletButtons.map((button) => (
              <Button
                key={button.id}
                type="button"
                variant="outline"
                className="flex h-14 items-center justify-center p-0"
                onClick={button.disabled ? undefined : button.onClick}
                disabled={button.disabled}
              >
                {button.icon}
                <span className="text-md">{button.label}</span>
              </Button>
            ))}
          </div>

          <div className="flex items-center text-gray-500">
            <hr className="flex-grow border-t border-gray-300" />
            <span className="mx-2 text-xs font-medium">{separatorLabel}</span>
            <hr className="flex-grow border-t border-gray-300" />
          </div>

          <div className="space-y-4">
            <div className="space-y-1">
              <Label htmlFor={cardholder.id}>{cardholder.label}</Label>
              <Input
                id={cardholder.id}
                name={cardholder.name}
                placeholder={cardholder.placeholder}
                value={cardholder.value}
                onChange={(event) => cardholder.onChange(event.target.value)}
                inputMode={cardholder.inputMode}
                type={cardholder.type}
                disabled={cardholder.disabled}
              />
            </div>

            <div className="space-y-1">
              <Label htmlFor={cardNumber.id}>{cardNumber.label}</Label>
              <Input
                id={cardNumber.id}
                name={cardNumber.name}
                placeholder={cardNumber.placeholder}
                value={cardNumber.value}
                onChange={(event) => cardNumber.onChange(event.target.value)}
                inputMode={cardNumber.inputMode}
                type={cardNumber.type}
                disabled={cardNumber.disabled}
              />
            </div>

            <div className="space-y-1">
              <Label htmlFor={expiry.id}>{expiryCvvLabel}</Label>
              <div className="flex gap-4">
                <Input
                  id={expiry.id}
                  name={expiry.name}
                  placeholder={expiry.placeholder}
                  value={expiry.value}
                  onChange={(event) => expiry.onChange(event.target.value)}
                  inputMode={expiry.inputMode}
                  type={expiry.type}
                  disabled={expiry.disabled}
                />
                <Input
                  id={cvv.id}
                  name={cvv.name}
                  placeholder={cvv.placeholder}
                  value={cvv.value}
                  onChange={(event) => cvv.onChange(event.target.value)}
                  inputMode={cvv.inputMode}
                  type={cvv.type}
                  disabled={cvv.disabled}
                />
              </div>
            </div>
          </div>

          {checkout.href ? (
            <Button
              className="w-full"
              size="lg"
              asChild
              disabled={checkout.disabled}
            >
              <a
                href={checkout.href}
                target={checkout.target ?? "_blank"}
                rel={checkout.rel ?? "noopener noreferrer"}
              >
                {checkout.label}
              </a>
            </Button>
          ) : (
            <Button
              className="w-full"
              size="lg"
              type="button"
              onClick={checkout.onClick}
              disabled={checkout.disabled}
            >
              {checkout.label}
            </Button>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
